import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { _axios } from "../../utils/axios";
import Multiselect from "multiselect-react-dropdown";
import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import userImg from "../../assets/images/user.svg";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { DocumentPreviewDialog } from "../../components/CommonComponents/DialogConfirmation/DocPreviewDialog";
import { DocDecisionL1Button, DownloadButton, NocL1Button, ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { BASE_URL } from "../../constants/Constants";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel1 } from "../../hoc/isLevel1";
import { TApplicationField } from "../../hooks/application-fields.hooks";
import {
  TApplication,
  TPopulatedForm,
  useApplicationFormSteps
} from "../../hooks/applications.hooks";
import { useToggle } from "../../hooks/common";
import {
  TReviewWithReviewer,
  useApplicationForLevel1,
  useForwardApplicationForLevel1,
  useLevel1AllReviews,
  useLevel1DecisionMaps,
  useLevel1LastReview,
  useLevel1RejectApplication,
  useLevel1ReturnApplication,
  useLevel1UpdateDocumentDecision,
  useLevel1UpdateNocDecision,
  useSaveLevel1Review
} from "../../hooks/level-1-application-approval.hooks";
import { useAutoFillDemandNoteData } from "../../hooks/level-3-application-approval.hooks";
import { downloadResource } from "../../utils/common";
import { formatDate } from "../../utils/date";
import { RenderAllReviews } from "./RenderAllReviews";

export const LevelOneApplicationViewDocsAndConfirm = isLevel1(() => {
  const location = useLocation();
  const [screen, setScreen] = useState<"docs" | "review" | "generate-doc">(
    "docs"
  );



  const id = +location.state?.id;
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const decisionMapQuery = useLevel1DecisionMaps({ id });

  const {
    isError: isError1,
    isFetching: isFetching1,
    data: application,
  } = useApplicationForLevel1({ id });

  const {
    data: result,
    isError: isError2,
    isFetching: isFetching2,
  } = useApplicationFormSteps(Number(application?.name_of_service));

  const autoFill = useAutoFillDemandNoteData();

  // const serviceQuery = useNameOfServiceField();
  // const nameOfService = serviceQuery.data?.options?.length ?
  //   serviceQuery.data.options.find((e) => e?.id === application?.name_of_service) : ''

  // const condition = useServiceConditionField({ name_of_service: (nameOfService as TApplicationFieldOption)?.name })

  const steps = result?.data || [];



  const isFetching = isFetching1 || isFetching2 || decisionMapQuery.isLoading;
  const isError =
    decisionMapQuery.isError ||
    isError1 ||
    isError2 ||
    (!isFetching && (!application || !steps?.length));

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    setStuff(
      <div className="md:ml-auto md:mr-8">
        <p className="text-grey-600">
          <span className="w-28 font-semibold inline-block">
            Application ID
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span>{application?.applicationId || "Nil"}</span>
        </p>
        <p className="text-grey-600 flex items-center">
          <span className="w-28 text-grey-600 font-semibold inline-block">
            Status
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span className="text-primary">{application?.status || "Nil"}</span>
        </p>
      </div>
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  const { documentDecisions, nocDecisions } = decisionMapQuery.data || {};

  if (isNaN(id)) {
    return <Navigate to={"/user-applications"} replace />;
  }

  if (isFetching || isError || !application) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  let toRender = null;

  switch (screen) {
    case "docs": {
      toRender = (
        <ViewAndValidateDocumentsAndNOCs
          application={application}
          steps={steps}
          documentDecisions={documentDecisions}
          nocDecisions={nocDecisions}
          onNext={() => setScreen("review")}
        />
      );
      break;
    }
    case "review": {
      toRender = (
        <Review
          steps={steps}
          application={application}
          onBack={() => setScreen("docs")}
          onNext={() => setScreen("generate-doc")}
          allDocsApproved={Object.values(documentDecisions || {})
            .concat(Object.values(nocDecisions || {}))
            .every((e) => !!e)}
        />
      );
      break;
    }

  }

  return (
    <>
      <div className="px-4 pb-4 md:p-4">
        <BoxComponent>
          <div className="bg-white p-2 rounded-2xl">{toRender}</div>
        </BoxComponent>
      </div>
    </>
  );
});

type GenerateDemandNoteProps = {
  onBack: () => unknown;
  application: TApplication & Record<string, unknown>;
  autoFill?: any;
  condition?: any;
};



type ReviewProps = {
  steps?: TPopulatedForm[];
  onBack: () => unknown;
  onNext: () => unknown;
  application: TApplication & Record<string, unknown>;
  allDocsApproved?: boolean;
};

const NON_SELECTABLE_FIELDS = [
  "noc_number",
  "noc_date",
  "noc_type",
  "name_of_service",
];

function Review({
  allDocsApproved,
  steps,
  onBack,
  onNext,
  application,
}: ReviewProps) {
  const qc = useQueryClient()
  const navigate = useNavigate();
  const [reviewsDialog, toggleReviewsDialog] = useToggle();
  const allReviews = useLevel1AllReviews({ id: application.id });
  const saveReviewMutation = useSaveLevel1Review();
  const lastReviewQuery = useLevel1LastReview({ id: application.id });
  const [fieldsToChange, setFieldsToChange] = useState<number[]>([]);
  const [comment, setComment] = useState("");
  const rejectMutation = useLevel1RejectApplication();
  const returnMutation = useLevel1ReturnApplication();
  const forwardMutation = useForwardApplicationForLevel1();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  const disabled =
    lastReviewQuery.isFetching ||
    saveReviewMutation.isPending ||
    rejectMutation.isPending ||
    returnMutation.isPending ||
    application.status !== "Pending Level 1 Review";

  const lastReviewData = lastReviewQuery.data;

  const allFields = useMemo(
    () =>
      (steps || [])
        .flatMap((e) => e.fields || [])
        .filter((e) => e?.active && !NON_SELECTABLE_FIELDS.includes(e.uid)),
    [steps]
  );

  const defaultSelected = useMemo(() => {
    return allFields.filter((e) =>
      lastReviewData?.fieldsToChange?.includes(e.id)
    );
  }, [allFields, lastReviewData]);

  useEffect(() => {
    setComment(lastReviewData?.review?.comment_text || "");
    setFieldsToChange(lastReviewData?.fieldsToChange || []);
  }, [lastReviewData]);

  if (lastReviewQuery.isLoading || lastReviewQuery.isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {lastReviewQuery.isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  let level1ReviewInfoToRender = null;

  if (allReviews.isLoading || allReviews.isError) {
    level1ReviewInfoToRender = (
      <div className="flex justify-center items-center p-8">
        {allReviews.isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  } else {
    const level1Review = (
      allReviews.data || []
    ).reduce<TReviewWithReviewer | null>(
      (acc, e) => (e?.level === "Level 1" ? e : acc),
      null
    );
    if (level1Review) {
      level1ReviewInfoToRender = (
        <div className="mb-8">
          {/* <div className="flex justify-between mb-4 items-center space-x-4">
            <h4 className="text-xl font-bold grow">Previous Reviewer Remark</h4>
            <img src={userImg} height={36} width={36} alt="profile-pic" />
            <div>
              <p className="text-base font-semibold">
                {level1Review.reviewer?.name || "--"}
              </p>
              <p className="text-xs">{formatDate(level1Review.date || "")}</p>
            </div>
          </div>
          <h6 className="mb-2 text-lg font-bold">Level 1 Reviewer Remark</h6>
          <p className="text-base">{level1Review.comment_text}</p> */}
          <h4 className="text-xl font-bold mb-4">Previous Reviewer Remark</h4>
          <div className="flex justify-between  items-center space-x-4">
            <div className="grow">
              <h6 className="mb-2 text-lg font-bold">
                Level 1 Reviewer Remark
              </h6>
              <p className="text-base">{level1Review.comment_text}</p>
            </div>
            <img src={userImg} height={36} width={36} alt="profile-pic" />
            <div>
              <p className="text-base font-semibold">
                {level1Review.reviewer?.name || "--"}
              </p>
              <p className="text-xs">{formatDate(level1Review.date || "")}</p>
            </div>
          </div>
        </div>
      );
    }
  }

  const user_id = application?.userId;


  const fetchUserInfo = async () => {
    try {
      const response = await _axios.post(`${BASE_URL}/api/levelOneMail/userinfo`, {
        userId: user_id,
      });
      setUserEmail(response.data.userInfo.email);
      setUserName(response.data.userInfo.name);
    } catch (error) {
      console.error("Failed to fetch user info", error);
    }
  };


  fetchUserInfo();


  const handleForward = async () => {
    try {
      const payload = {
        body: {
          email: userEmail,
          name: userName,
        },
        application_id: application?.applicationId,
      };
      console.log("Payload being sent:", payload); // Debugging line
      await _axios.post(`${BASE_URL}/api/levelOneMail/send`, payload);
    } catch (error) {
      console.error("Failed to forward application or send email", error);
    }
  };


  console.log('user id :', application?.userId);

  return (
    <>
      {level1ReviewInfoToRender}
      <div className="grid grid-cols-5 gap-4 items-center">
        <div className="col-span-5 md:col-span-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm md:text-base font-bold">
              Level 1 Reviewer Remark <span className="text-red-500">*</span>
            </label>
            <button
              className="text-sm md:text-base font-bold disabled:text-gray-500"
              disabled={!allReviews.data?.length}
              onClick={toggleReviewsDialog}
            >
              <i className="fa fa-history"></i>
              Previous Reviews
            </button>
          </div>
          <textarea
            placeholder="Add Comment"
            value={comment}
            disabled={disabled}
            rows={5}
            className="h-auto w-100"
            onChange={(e) => setComment(e.target.value)}
          />
          <Multiselect
            disable={disabled}
            key={`multiselect-${lastReviewQuery.isLoading}`}
            style={{
              multiselectContainer: {
                border: "1px solid #dee2e6",
                "border-radius": "8px",
              },
            }}
            selectedValues={defaultSelected}
            placeholder="Select Change Fields"
            options={allFields}
            onSelect={(list: TApplicationField[]) =>
              setFieldsToChange(list.map((e) => e.id))
            }
            onRemove={(list: TApplicationField[]) =>
              setFieldsToChange(list.map((e) => e.id))
            }
            displayValue="label"
          />

          <div className="flex-wrap md:flex-nowrap flex justify-center gap-4 items-center mt-8">
            <button
              type="button"
              onClick={onBack}
              disabled={saveReviewMutation.isPending}
              className="bg-black px-10 py-2 rounded-lg md:rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 basis-full md:basis-auto"
            >
              Back
            </button>
            <button
              type="button"
              disabled={disabled || !comment?.trim()}
              onClick={() => {
                saveReviewMutation.mutate({
                  applicationId: application.id,
                  comment,
                  fieldsToChange,
                });
              }}
              className="bg-primary px-10 py-2 rounded-lg md:rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 basis-full md:basis-auto"
            >
              {saveReviewMutation.isPending ? "Saving" : "Save"}
            </button>

          </div>
        </div>
        <div className="col-span-5 md:col-span-1 space-y-4">
          <button
            type="button"
            disabled={
              disabled ||
              !lastReviewQuery.data?.review?.id ||
              (allDocsApproved && !fieldsToChange.length)
            }
            className="bg-red-400 w-full py-2 rounded-lg font-bold text-white block disabled:bg-gray-300 disabled:text-gray-500"
            onClick={() =>
              returnMutation.mutate(
                {
                  applicationId: application.id,
                  application_id: application.applicationId,
                  user_id: application.userId,
                },
                {
                  onSuccess: () =>
                    navigate("/level-1-applications"),
                  onSettled: () => { }
                }
              )
            }
          >
            {returnMutation.isPending ? "Returning" : "Return"}
          </button>
          <button
            type="button"
            disabled={
              disabled ||
              !lastReviewData?.review?.id ||
              !allDocsApproved ||
              !!fieldsToChange?.length
            }
            onClick={() =>
              forwardMutation.mutate(
                {
                  applicationId: application.id,
                  reviewId: lastReviewData?.review?.id ?? 0, // Assuming reviewId is coming from the last review data
                },
                {
                  onSuccess: () => {
                    handleForward();
                    navigate("/level-1-applications");
                  },
                }
              )}
            style={{ backgroundColor: '#25B101' }}
            className="w-full py-2 rounded-lg font-bold text-white block disabled:bg-gray-300 disabled:text-gray-500"
          >
            {forwardMutation.isPending ? "Forwarding" : "Forward"}
          </button>

        </div>
      </div>
      {reviewsDialog && (
        <RenderAllReviews
          onClose={toggleReviewsDialog}
          reviews={allReviews.data || []}
        />
      )}
    </>
  );
}

type ViewAndValidateDocumentsAndNOCsProps = {
  nocDecisions?: Record<string, boolean>;
  documentDecisions?: Record<string, boolean>;
  steps?: TPopulatedForm[];
  application: TApplication & Record<string, unknown>;
  onNext?: () => unknown;
};

function ViewAndValidateDocumentsAndNOCs({
  steps,
  application,
  nocDecisions,
  documentDecisions,
  onNext,
}: ViewAndValidateDocumentsAndNOCsProps) {
  const status = application?.status;
  const id = application?.id;
  const [document, setDocument] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const navigate = useNavigate();
  const docDecisionMutation = useLevel1UpdateDocumentDecision();
  const nocDecisionMutation = useLevel1UpdateNocDecision();
  const { isPending: updatingNocDecision, mutate: updateNocDecision } =
    nocDecisionMutation;

  const nocColumns = useMemo((): TableColumn<Record<string, unknown>>[] => {
    const noc = steps?.find((e) => e.type === "NOC");
    if (!noc) return [];
    return [
      { name: "Sr. No.", selector: (_, i) => (i || 0) + 1, center: true, grow: 0.5 },
      {
        name: "NOC Type",
        selector: (d) =>
          String(
            (noc.fields?.find((e) => e.uid === "noc_type")?.options || []).find(
              (e) => e.id === d.noc_type
            )?.name || "-"
          ),
        grow: 3,
        center: true,
      },
      {
        name: "NOC No",
        selector: (d) => String(d?.noc_number || "-"),
        center: true,
        grow: 2,
        width: "120px",
      },
      {
        name: "NOC Date",
        selector: (d) => formatDate(String(d?.noc_date || "x")),
        center: true,
        grow: 2,
        width: "130px",
      },
      // {
      //   name: "Level 1",
      //   center: true,
      //   cell: () => (
      //     <i className="m-0 fa fa text-lg fa-check-square-o text-green-400"></i>
      //   ),
      // },
      {
        name: "View",
        center: true,
        width: "180px",
        cell: (d, i) => (
          <div className="flex space-x-4">
            {/* <button
              className="text-lg"
              disabled={
                updatingNocDecision || status !== "Pending Level 1 Review"
              }
              onClick={() => {
                updateNocDecision({
                  applicationId: id,
                  decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                  nocId: Number(d.id),
                });
              }}
            >
              {nocDecisions?.[String(d?.id)] === false ? (
                <i className="m-0 fa fa-window-close-o text-red-500"></i>
              ) : (
                <i className="m-0 fa fa-check-square-o text-green-400"></i>
              )}
            </button>

            <button
              className="text-lg"
              onClick={() =>
                setDocument({
                  url: String(d?.noc_doc),
                  name: String(
                    noc.fields
                      ?.find((e) => e.uid === "noc_type")
                      ?.options?.find((e) => e.id === d.noc_type)?.name || ""
                  ),
                })
              }
            >
              <i className="m-0 fa fa-eye"></i>
            </button>
            <button
              onClick={() => downloadResource(String(d?.noc_doc))}
              className="text-lg"
            >
              <i className="m-0 fa fa-download"></i>
            </button> */}
            {/* <div className="flex space-x-2"> */}
            {/* <button
              className="text-lg"
              disabled={
                updatingNocDecision || status !== "Pending Level 1 Review"
              }
              onClick={() => {
                updateNocDecision({
                  applicationId: id,
                  decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                  nocId: Number(d.id),
                });
              }}
            >
              {nocDecisions?.[String(d?.id)] === false ? (
                <i className="m-0 fa fa-window-close-o text-red-500 bg-red-200 p-3 rounded-md"></i>
              ) : (
                <i className="m-0 fa fa-check-square-o text-green-400 bg-blue-200 p-3 rounded-md"></i>
              )}
            </button> */}
            <NocL1Button
              isChecked={nocDecisions?.[String(d?.id)] !== false} // Change this line
              isUpdating={updatingNocDecision}
              statusCheck={status}
              onClick={() => {
                updateNocDecision({
                  applicationId: id,
                  decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                  nocId: Number(d.id),
                });
              }}
              tooltipText="Toggle NOC Decision"
            />
            <ViewButton
              onClick={() =>
                setDocument({
                  url: String(d?.noc_doc),
                  name: String(
                    noc.fields
                      ?.find((e) => e.uid === "noc_type")
                      ?.options?.find((e) => e.id === d.noc_type)?.name || ""
                  ),
                })
              }
            />
            <DownloadButton
              onClick={() => downloadResource(String(d?.noc_doc))}
            />
            {/* </div> */}
          </div>
        ),
      },
    ];
  }, [status, steps, updatingNocDecision, nocDecisions, updateNocDecision]);

  const { isPending: updatingDocDecision, mutate: updateDocDecision } =
    docDecisionMutation;

  const documentTable = useMemo((): {
    docs: { id: number; name: string; url: string }[];
    documentColumns: TableColumn<{
      id: number;
      name: string;
      url: string;
    }>[];
  } => {
    const docForm = steps?.find((e) => e.type === "Documents");

    if (!docForm) return { docs: [], documentColumns: [] };
    return {
      docs: Object.entries(application?.documents || {})
        .map(([k, { document: url, id: documentId }]) => {
          const hit = docForm?.documentConfigs?.find(
            (e) => e.document_config_id === +k
          )?.document_label;

          if (!hit) return null;
          return { name: hit, url, id: documentId };
        })
        .filter((x): x is NonNullable<typeof x> => !!x),
      documentColumns: [
        { name: "Sr. No.", selector: (_, i) => (i || 0) + 1, center: true, grow: 0.5 },
        {
          name: "Document Name",
          // selector: (d) => d.name, 
          cell: (d) => <span className="text-center">{d.name}</span>,
          center: true,
          // width: "380px",
          grow: 3,
        },
        // {
        //   name: "Level 1",
        //   center: true,
        //   cell: () => (
        //     <i className="m-0 fa fa text-lg fa-check-square-o text-green-400"></i>
        //   ),
        // },
        {
          name: "View",
          grow: 1,
          width: "180px",
          center: true,
          cell: (d, i) => (
            <div className="flex space-x-4">
              {/* <button
                className="text-lg"
                disabled={
                  updatingDocDecision || status !== "Pending Level 1 Review"
                }
                onClick={() => {
                  updateDocDecision({
                    applicationId: id,
                    decision:
                      documentDecisions?.[String(d?.id)] === false ? 1 : 0,
                    docId: Number(d.id),
                  });
                }}
              >
                {documentDecisions?.[String(d?.id)] === false ? (
                  <i className="m-0 fa fa-window-close-o text-red-500"></i>
                ) : (
                  <i className="m-0 fa fa-check-square-o text-green-400"></i>
                )}
              </button> */}
              {/* <button
                className="text-lg"
                onClick={() =>
                  setDocument({
                    url: String(d?.url),
                    name: String(d?.name || ""),
                  })
                }
              >
                <i className="m-0 fa fa-eye"></i>
              </button> */}
              {/* <button
                onClick={() => downloadResource(String(d?.url))}
                className="text-lg"
              >
                <i className="m-0 fa fa-download"></i>
              </button> */}
              <DocDecisionL1Button
                isChecked={documentDecisions?.[String(d?.id)] !== false}
                isUpdating={updatingDocDecision}
                statusCheck={status}
                onClick={() => {
                  updateDocDecision({
                    applicationId: id,
                    decision: documentDecisions?.[String(d?.id)] === false ? 1 : 0,
                    docId: Number(d.id),
                  });
                }}
                tooltipText="Toggle Document Decision"
              />
              <ViewButton
                onClick={() =>
                  setDocument({
                    url: String(d?.url),
                    name: String(d?.name || ""),
                  })
                }
              />
              <DownloadButton
                onClick={() => downloadResource(String(d?.url))}
              />
            </div>
          ),
        },
      ],
    };
  }, [
    steps,
    status,
    application,
    updateDocDecision,
    updatingDocDecision,
    documentDecisions,
  ]);

  const nothingToShow = !!(
    !(application?.nocs as Array<Record<string, unknown>>)?.length &&
    !(documentTable?.docs as Array<Record<string, unknown>>)?.length
  );



  const isImageUrl = (doc: { url: string }) => {
    return /\.(jpg|jpeg|png|svg)$/.test(doc.url);
  };

  return (
    <>
      <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
        Documents
      </h4>
      {!!nocColumns?.length &&
        !!(application?.nocs as Record<string, unknown>[])?.length && (
          <><p className=" text-grey-600 text-base font-semibold">
            Noc Table:
          </p><DataTable
              keyField="url"
              className="mt-4 border border-none rounded-none"
              columns={nocColumns}
              customStyles={tableCustomStyles}
              data={(application?.nocs as Record<string, unknown>[]) || []} /></>
        )}
      {!!documentTable.docs?.length && (
        <><p className="text-grey-600 text-base mt-4 font-semibold">
          Document Table:
        </p><DataTable
            keyField="url"
            className="mt-4 border border-none rounded-none"
            data={documentTable.docs}
            customStyles={tableCustomStyles}
            columns={documentTable.documentColumns} /></>
      )}
      {nothingToShow && (
        <div className="flex justify-center items-center py-8">
          <h6 className="text-xl font-semibold">No Documents</h6>
        </div>
      )}
      {!!onNext && (
        <div className="flex justify-center space-x-4 items-center pt-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={
              nocDecisionMutation.isPending || docDecisionMutation.isPending
            }
            className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={
              nocDecisionMutation.isPending || docDecisionMutation.isPending
            }
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Next
          </button>
        </div>
      )}
      {!!document && (
        <DocumentPreviewDialog
          open={!!document}
          onClose={() => setDocument(null)}
        >
          {isImageUrl(document) ? (
            <img
              src={document.url}
              className="object-contain mx-auto"
              alt={document.name}
            />
          ) : (
            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
              <Viewer fileUrl={document.url + '#toolbar=0&zoom=full'} />
            </Worker>
          )}
        </DocumentPreviewDialog>
      )}
    </>
  );
}
